import type { StaticImageData } from 'next/image';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { ImageCarousel } from '@/components/ImageCarousel';
import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { Loader } from '@/components/Loader';
import { ProductLabel } from '@/components/ProductLabel';
import type { ProductMediaFragment } from '@/graphql/saleor/api';
import type { GetImageDimensions } from '@/lib/images';
import type { ProductTypeOptions } from '@/lib/product';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe, WithChildren } from '@/lib/types';

import type { Gallery } from '../ImageCarousel/context';
import { ProductCard } from '../ProductCard';
import { getMediaDimensions } from './utils';

import * as S from './ImagesGallery.styles';

interface StaticImageSrcAsUrl {
  url: StaticImageData['src'];
}

type ProductImage = ProductMediaFragment;
type CustomImage = StaticImageSrcAsUrl & { alt: string };
export type Media = ProductImage | CustomImage;

type ProductImageLarge = Pick<ProductMediaFragment, 'url'> &
  Partial<GetImageDimensions>;
type CustomImageLarge = StaticImageSrcAsUrl &
  Partial<Pick<StaticImageData, 'width' | 'height'>>;
export type MediaLarge = ProductImageLarge | CustomImageLarge;

interface CommonProps {
  media: Maybe<Media[]>;
  name?: string | undefined | null;
  price?: number | undefined;
  productType?: ProductTypeOptions;
  shareableProductData?: Record<PropertyKey, string | null | undefined>;
  sku?: string | undefined | null;
  tag?: Maybe<ReactNode>;
  theCurrency?: string | undefined;
  withIcons?: boolean;
}
interface PropsWithGallery extends CommonProps, WithChildren<false> {
  alt?: string;
  gallery: Gallery;
  mediaLarge: Maybe<MediaLarge[]>;
}

interface PropsWithoutGallery extends CommonProps, WithChildren<false> {
  alt?: string;
  gallery?: never;
  mediaLarge?: never;
}

export type ImagesGalleryProps = PropsWithGallery | PropsWithoutGallery;

export type Image = {
  large?: MediaLarge;
  thumb: Media;
};

export const ImagesGallery = ({
  alt,
  children,
  gallery,
  media,
  mediaLarge,
  name,
  price,
  productType = 'strap',
  shareableProductData,
  sku,
  tag,
  theCurrency,
  withIcons = true,
}: ImagesGalleryProps) => {
  const [images, setImages] = useState<Image[]>(
    media?.map((item, i) => {
      if (!mediaLarge) {
        return {
          thumb: item,
        };
      } else {
        return {
          thumb: item,
          large: { url: mediaLarge[i].url },
        };
      }
    }) ?? [],
  );

  const [loader, setLoader] = useState(false);
  const isInitial = useRef(true);

  const isPhotoSwipeReady = images.every(({ large }) => !!large?.height);
  const hasMedia = !!media?.length;

  useEffect(() => {
    if (isInitial.current) {
      isInitial.current = false;
    } else {
      // only show loader when media update
      setLoader(true);
    }
    if (media?.length && mediaLarge?.length) {
      (async () => {
        const imagesWithDimensions = await getMediaDimensions(
          media,
          mediaLarge,
        );
        setImages(imagesWithDimensions);
        setLoader(false);
      })();
    } else {
      setImages([...images]); // Rerender must be triggered to have proper references inside Carousel.tsx
      setLoader(false);
    }
  }, [media, mediaLarge]);

  return (
    <S.GalleryWrapperCol
      data-test-id='ImagesGalleryWrapper'
      md={6}
      {...generateDataTestId('imagesGallery', 'wrapper')}
    >
      {children}

      {withIcons && sku && (
        <S.GalleryIcons>
          <S.LabelWrapper>{tag && <ProductLabel text={tag} />}</S.LabelWrapper>
          <S.ActionsWrapper flexDirection='column'>
            <ProductCard.Favorite
              data-test-id='ImagesGalleryFavorite'
              name={name}
              price={price}
              sku={sku}
              theCurrency={theCurrency}
              type={productType}
            />
            {shareableProductData && (
              <ProductCard.Share
                positionRight
                shareableProductData={shareableProductData}
              />
            )}
          </S.ActionsWrapper>
        </S.GalleryIcons>
      )}

      <ImageCarousel
        key={sku}
        gallery={gallery}
        isProcessing={!isPhotoSwipeReady}
      >
        <ImageCarousel.Carousel productType={productType}>
          {hasMedia ? (
            images.map(({ large, thumb }, i) => (
              <ImageCarousel.CarouselItem
                key={thumb.url}
                data-testid={`ImagesGalleryCarouselItem-${i}`}
                {...(large && {
                  height: large.height,
                  largeSrc: large.url,
                  width: large.width,
                })}
              >
                {thumb.url &&
                  (loader ? (
                    <S.CenterLoader data-testid='GLigor'>
                      <Loader />
                    </S.CenterLoader>
                  ) : (
                    <ProductCard.Image
                      alt={thumb.alt || `${alt} - image ${i + 1}`}
                      className='carousel-image'
                      data-testid={`ImagesGalleryImage-${i}`}
                      disableLoader={i === 0}
                      height={680}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      sizes='(max-width: 991px) 40vw,
                        (max-width: 1261px) 55vw,
                        680px'
                      src={thumb.url}
                      width={680}
                    />
                  ))}
              </ImageCarousel.CarouselItem>
            ))
          ) : (
            <ImagePlaceholder
              className='carousel-image'
              data-testid='ImagesGalleryImagePlaceholder'
              productType={productType}
            />
          )}
        </ImageCarousel.Carousel>
      </ImageCarousel>
    </S.GalleryWrapperCol>
  );
};
